import React, { useEffect, useId, useRef, useState } from 'react';
import * as ExpoRouter from 'expo-router';
import { formatVideoDuration, getTutorialVideos } from '@/lib/tutorialVideos';
import useRequireAuth from '@/hooks/useRequireAuth';
import './TutorialVideo.css';

export const AUTOPLAY_SECONDS = 5;
const AUTOPLAY_PARAM = 'autoplay';

function withAutoplay(href) {
  return `${href}${href.includes('?') ? '&' : '?'}${AUTOPLAY_PARAM}=1`;
}

function navigate(href) {
  if (ExpoRouter.router?.push) ExpoRouter.router.push(href);
  else window.location.assign(href);
}

function goNext(next) {
  if (next.onSelect) next.onSelect();
  else navigate(next.autoplay ? withAutoplay(next.href) : next.href);
}

export function requestedAutoplay() {
  try { return new URLSearchParams(window.location.search).get(AUTOPLAY_PARAM) === '1'; } catch { return false; }
}

// MP4s come from the CDN without CORS; posters may also ship with the website.
// Captions are served from this origin, so the video doesn't need crossorigin.
export default function TutorialVideo({ video, priority = false, next = null, autoplay = false, variant = 'default', playButton = true, signup = null }) {
  const id = useId();
  const ref = useRef(null);
  const [ended, setEnded] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [ctaVisible, setCtaVisible] = useState(false);
  const [started, setStarted] = useState(false);
  const [captionsOn, setCaptionsOn] = useState(false);
  const requireAuth = useRequireAuth();

  useEffect(() => {
    const track = ref.current?.textTracks?.[0];
    if (track) track.mode = captionsOn ? 'showing' : 'hidden';
  }, [captionsOn, started]);

  useEffect(() => {
    if (!autoplay || !ref.current) return;
    ref.current.scrollIntoView?.({ block: 'center' });
    ref.current.play?.()?.catch?.(() => {});
  }, [autoplay]);

  // Navigating away keeps the previous route mounted but hidden, so the
  // video would keep playing off screen.
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const observer = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver(() => {
      if (!el.paused && el.getClientRects().length === 0) el.pause();
    });
    observer?.observe(el);
    return () => {
      observer?.disconnect();
      el.pause?.();
    };
  }, [video?.src]);

  useEffect(() => {
    if (countdown === null) return undefined;
    if (countdown <= 0) {
      setCountdown(null);
      goNext(next);
      return undefined;
    }
    const timer = setTimeout(() => setCountdown(value => (value === null ? null : value - 1)), 1000);
    return () => clearTimeout(timer);
  }, [countdown, next]);

  if (!video?.src) return null;

  const onEnded = () => {
    setEnded(true);
    if (next?.autoplay) setCountdown(AUTOPLAY_SECONDS);
  };
  // The closing card's button is part of the video; this real button sits over it.
  const cta = video.cta;
  const onTimeUpdate = event => {
    if (!cta) return;
    const visible = event.currentTarget.currentTime >= cta.start;
    if (visible !== ctaVisible) setCtaVisible(visible);
  };
  const onCta = () => {
    ref.current?.pause?.();
    if (cta.action === 'lessons') {
      navigate(next?.href || '#learn-lessons');
      return;
    }
    if (cta.action === 'link' && cta.href) {
      navigate(cta.href);
      return;
    }
    requireAuth(() => navigate('/app'), {
      source: 'tutorial_video',
      title: 'Create your free Gab AI account',
      subtitle: 'Sign up or log in to try what you just watched.',
      onAuthSuccess: () => navigate('/app'),
    });
  };
  const replay = () => {
    setEnded(false);
    setCountdown(null);
    if (ref.current) { ref.current.currentTime = 0; ref.current.play?.()?.catch?.(() => {}); }
  };

  return (
    <figure id={`tutorial-video-${video.slug}`} className={`tutorial-video tutorial-video-${variant}`} aria-labelledby={`${id}-title`}>
      <figcaption className={variant === 'hero' || variant === 'lesson' ? 'tutorial-video-sr' : 'tutorial-video-heading'}>
        <span className="tutorial-video-eyebrow">Video tutorial · {formatVideoDuration(video.duration)}</span>
        <h3 id={`${id}-title`}>{video.title}</h3>
        {!!video.description && <p>{video.description}</p>}
      </figcaption>
      <div className={`tutorial-video-frame${video.posterLayout === 'centered' ? ' tutorial-video-centered' : ''}${started ? ' is-started' : ''}`}>
        <video
          ref={ref}
          controls={!playButton || started}
          playsInline
          preload={priority || autoplay ? 'metadata' : 'none'}
          poster={video.poster}
          width={1920}
          height={1080}
          aria-labelledby={`${id}-title`}
          onEnded={onEnded}
          onPlay={() => { setStarted(true); setEnded(false); setCountdown(null); }}
          onTimeUpdate={onTimeUpdate}
          onSeeked={onTimeUpdate}
        >
          <source src={video.src} type="video/mp4" />
          {!!video.captions && <track kind="captions" src={video.captions} srcLang="en" label="English" />}
          <a href={video.src}>Download the video</a>
        </video>
        {!!video.captions && !ended && (
          <button
            type="button"
            className={`tutorial-video-cc${captionsOn ? ' is-on' : ''}`}
            aria-pressed={captionsOn}
            aria-label={captionsOn ? 'Turn captions off' : 'Turn captions on'}
            title={captionsOn ? 'Captions on' : 'Captions off'}
            onClick={() => setCaptionsOn(on => !on)}
          >
            CC
          </button>
        )}
        {playButton && !started && !ended && (
          <button type="button" className="tutorial-video-play" aria-label={`Play video, ${formatVideoDuration(video.duration)}`} onClick={() => ref.current?.play?.()?.catch?.(() => {})}>
            <span className="tutorial-video-play-control">
              <span className="tutorial-video-play-icon" aria-hidden="true" />
              <span className="tutorial-video-play-label">
                {video.posterLayout === 'centered'
                  ? 'Watch the tour'
                  : `Play video · ${formatVideoDuration(video.duration)}`}
              </span>
            </span>
          </button>
        )}
        {cta && ctaVisible && !ended && (
          <button
            type="button"
            className="tutorial-video-cta"
            style={{ left: `${cta.left}%`, top: `${cta.top}%`, width: `${cta.width}%`, height: `${cta.height}%` }}
            onClick={onCta}
          >
            <span className="tutorial-video-sr">{cta.label}</span>
          </button>
        )}
        {ended && next && (
          <div className={`tutorial-video-next${signup ? ' has-signup' : ''}`} role="group" aria-label={signup ? 'What next' : 'Up next'}>
            <span className="tutorial-video-next-eyebrow">{signup ? signup.eyebrow || 'Thanks for watching' : 'Up next'}</span>
            <strong className="tutorial-video-next-title">{signup ? signup.title || next.label : next.label}</strong>
            <p className="tutorial-video-next-status" aria-live="polite">
              {countdown !== null ? `Starting in ${countdown}…` : next.autoplay ? 'Autoplay paused.' : ''}
            </p>
            <div className="tutorial-video-next-actions">
              {signup && (
                <button type="button" className="tutorial-video-next-primary" onClick={() => { setCountdown(null); navigate(signup.href); }}>
                  {signup.label}
                </button>
              )}
              <button type="button" className={signup ? undefined : 'tutorial-video-next-primary'} onClick={() => { setCountdown(null); goNext(next); }}>
                {next.action || (next.autoplay ? 'Play next' : 'Go to next page')} <span aria-hidden="true">→</span>
              </button>
              {countdown !== null
                ? <button type="button" onClick={() => setCountdown(null)}>Cancel</button>
                : <button type="button" className={signup ? 'tutorial-video-next-link' : undefined} onClick={replay}>
                    {signup && <span aria-hidden="true">↺ </span>}Replay
                  </button>}
            </div>
          </div>
        )}
      </div>
    </figure>
  );
}

function siblingStop(video) {
  return {
    label: video.title,
    autoplay: true,
    onSelect: () => {
      const el = document.querySelector(`#tutorial-video-${video.slug} video`);
      el?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      el?.play?.()?.catch?.(() => {});
    },
  };
}

export function TutorialVideos({ guideType, slug, next = null, variant = 'default' }) {
  const list = getTutorialVideos(guideType, slug);
  const [autoplay] = useState(requestedAutoplay);
  if (!list.length) return null;
  return (
    <div className={`tutorial-videos tutorial-videos-${variant}`}>
      {list.map((video, index) => (
        <TutorialVideo
          key={video.slug}
          video={video}
          variant={variant}
          priority={index === 0}
          autoplay={autoplay && index === 0}
          next={index === list.length - 1 ? next : siblingStop(list[index + 1])}
        />
      ))}
    </div>
  );
}
